import React, { memo } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface SectionHeaderProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  isCollapsed?: boolean;
  onCollapsedChange?: (nextIsCollapsed: boolean) => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = memo(
  ({
    icon,
    title,
    subtitle,
    children,
    actions,
    isCollapsed = false,
    onCollapsedChange,
  }) => {
    const hasCollapsibleControl = Boolean(onCollapsedChange);
    const handleCollapsedToggle = () => onCollapsedChange?.(!isCollapsed);

    return (
      <Stack spacing={0.75}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          onClick={hasCollapsibleControl ? handleCollapsedToggle : undefined}
          sx={{
            width: "100%",
            cursor: hasCollapsibleControl ? "pointer" : "default",
          }}
        >
          {icon && (
            <Paper
              variant="outlined"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                borderColor: "divider",
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
            </Paper>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ lineHeight: 1.15 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ ml: "auto" }}
            onClick={(event) => event.stopPropagation()}
          >
            {actions}
            {hasCollapsibleControl && (
              <IconButton
                size="small"
                aria-label={isCollapsed ? "Expandir seção" : "Recolher seção"}
                onClick={handleCollapsedToggle}
              >
                {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Stack>
        </Stack>
        {children && (
          <Collapse in={!isCollapsed} timeout="auto" unmountOnExit={false}>
            <Box sx={{ pt: 1 }}>{children}</Box>
          </Collapse>
        )}
      </Stack>
    );
  },
);

SectionHeader.displayName = "SectionHeader";

export default SectionHeader;

